// xt this is original file of the BSG Console system 2018
// xt I will put notes as I discover info
//   the <i> tags are from the google Material+icon system, that is imported in the index.css file
//    these <i> icons create the embedded icons on the circle buttons for the USER, NOTES, LOG, DELETE actions on console
//   must use the className='material-icons' in order to access them. For more info see- https://materializecss.com/icons.html
// 20250617: xt - added null checks for incident and chat_summary
// 20250801: xt needed to update for new backend??
// 20251215: xt add feature to auto message on acknowldedge button, 
// 20260609: xt add new feature to block a user from submitting a report
// 20260814: xt add new feature to email a single report with activity circle button

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import './IncidentAction.css';
import Globals from '../../utils/globals'; // add at top if not already

import { showModal } from '../../actions/modal';
import { postIncidentState, updateIncidentStatus, deleteReport } from '../../actions/reportSummaries';
import { postNoteMessage } from '../../actions/messages'; // 20251216 added for ack auto message feature

// normalize ObjectId-ish values to plain strings (added to fix code for status ifx)
const idOf = (x) =>
  typeof x === "string" ? x : x?.$oid || (x && x.toString ? x.toString() : "");


// FUNCTIONAL COMPONENT DECLARATION SECTION (a pointer to function in memory)
const IncidentAction = ({ reportSchemaId, languageCode, incident }) => {
  console.log('Incident passed into IncidentAction:', incident);

  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const tokenRef = useRef(null); // ✅ Store token once


  const [reporterBlocked, setReporterBlocked] = useState(false);
  const [checkingBlockStatus, setCheckingBlockStatus] = useState(false);


  // these are globals that all other pages will use
  const profile = useSelector((state) => state.profile);
  const agency = useSelector((state) => state.agency);
  console.log('agency.details.report_schemas:', agency.details?.report_schemas);



  // --- 20251216 Auto-Acknowledge Note settings (per agency/client) ---
  const autoAckConfig = agency?.details?.auto_acknowledge_note || {};
  const autoAckEnabled = !!autoAckConfig.enabled;
  // Should we hide the manual "User Note" button for this agency?
  const hideUserNoteButton =
    !!(
      autoAckConfig.hide_user_note_button ||  // preferred name
      autoAckConfig.no_user_note             // optional alias if you ever use it
    );

  // Choose language for canned note
  // languageCode from props is likely "en_US" / "es_US" — map to "en" / "es"
  const langFromProps = languageCode || 'en_US';
  const activeLang =
    langFromProps.startsWith('es') ? 'es'
      : langFromProps.startsWith('en') ? 'en'
        : 'en'; // default to English


  const autoAckMessage =
    (autoAckConfig.messages && autoAckConfig.messages[activeLang]) ||
    autoAckConfig.message ||            // optional single message fallback
    '';                                 // if nothing configured, feature is effectively off


  if (!incident) {
    console.warn('No incident provided to IncidentAction');
    return <div>No incident data for IncidentAction Section</div>;
  }
  console.log('profile', profile);
  console.log('app_metadata', profile?.app_metadata);

  const myAdmin = profile?.app_metadata.is_admin || false;
  console.log("myAdmin is,", myAdmin)

  let noDept = false;
  if (agency?.details?.assignees?.departments?.length > 0) {
    noDept = true;
  }

  //const reportStatuses = agency.details?.report_schemas?.report_statuses || [];
  //const currentStatusId = incident?.status_id;
  //const selectedStatus = reportStatuses.find(status => status._id === currentStatusId);

  //these avoid needing to pass reportStates, reportStatuses, etc. from a parent — 
  // you compute them cleanly in one place now.
  //Get the correct report schema object for this incident from agency:
  // NOTE: reportSchema was buried under agency.details by morons
  //const reportSchema = agency?.details?.report_schemas?.find(schema => schema._id === reportSchemaId) || {};
  const reportSchema =
    agency?.details?.report_schemas?.find(s => idOf(s._id) === idOf(reportSchemaId)) || {};

  // Agency display name used by the Email Report modal.
  // Prefer localized agency name, then older display-name shapes.
  const agencyName =
    (agency?.details?.name && (
      agency.details.name.en_US ||
      agency.details.name.es_US ||
      agency.details.name['en-US'] ||
      agency.details.name['es-US']
    )) ||
    agency?.details?.display_name ||
    agency?.details?.name ||
    agency?.display_name ||
    agency?.name ||
    'Transit Watch';


  // Get report states for dashboard only
  let reportStates = []
  let reportStatuses = []
  let selectedStatus = {}
  let selectedState = {}
  if (undefined !== reportSchema) {
    reportStates = reportSchema.report_states
      ? reportSchema.report_states.filter(state => state.types.find(type => type === 'dashboard'))
      : []

    reportStatuses = reportSchema.report_statuses
    // Get selected state and status
    selectedState = incident.state && reportSchema.report_states
      ? reportSchema.report_states.find(state => state._id === incident.state)
      : {}
    selectedStatus = selectedState && reportStatuses
      ? reportStatuses.find(status => status._id === selectedState.report_status_id)
      : {}
  }

  // change the console to let the dipathcer drive the status

  // --- STATUS-DRIVEN UI helpers (uses schema's report_statuses) ---
  const schemaStatuses = Array.isArray(reportStatuses) ? reportStatuses : (reportSchema?.report_statuses || []);
  const schemaStates = Array.isArray(reportStates) ? reportStates : (reportSchema?.report_states || []);

  // map status by type (new|open|closed) and by _id
  const statusByType = Object.fromEntries(schemaStatuses.map(s => [s.type, s]));
  const statusById = Object.fromEntries(schemaStatuses.map(s => [idOf(s._id), s]));

  // canonical status ids
  const NEW_STATUS_ID = idOf(statusByType.new?._id);
  const OPEN_STATUS_ID = idOf(statusByType.open?._id);
  const CLOSED_STATUS_ID = idOf(statusByType.closed?._id);

  // find a state id that corresponds to a given status id (for posting to backend)
  const stateForStatusId = (statusId) => {
    const sid = idOf(statusId);
    const st = schemaStates.find(s => idOf(s.report_status_id) === sid);
    return st ? idOf(st._id) : null;
  };

  // specific state ids we need to send
  const OPEN_STATE_ID = stateForStatusId(OPEN_STATUS_ID);
  const CLOSED_STATE_ID = stateForStatusId(CLOSED_STATUS_ID);

  // current incident status id (source of truth for UI)
  const curStatusId = idOf(incident.status_id || incident.status);

  // flags
  const isNew = curStatusId === NEW_STATUS_ID;
  const isOpen = curStatusId === OPEN_STATUS_ID;
  const isClosed = curStatusId === CLOSED_STATUS_ID;

  // dot color from schema (fallbacks)
  const newColor = statusById[NEW_STATUS_ID]?.color || "#B50D0C";
  const openColor = statusById[OPEN_STATUS_ID]?.color || "#4476C9";
  const closedColor = statusById[CLOSED_STATUS_ID]?.color || "#000000";
  const statusDotColor = isNew ? newColor : isOpen ? openColor : closedColor;

  // selectedStatus (for the pill) should come from STATUS, not state
  const selectedStatusForPill =
    statusById[curStatusId] ||
    (isNew ? statusById[NEW_STATUS_ID] : isOpen ? statusById[OPEN_STATUS_ID] : statusById[CLOSED_STATUS_ID]);



  // ✅ Get token once on mount
  useEffect(() => {
    const fetchToken = async () => {
      try {
        if (!tokenRef.current) {
          const token = await getAccessTokenSilently();
          tokenRef.current = token;
        }
      } catch (err) {
        console.error('Failed to get token in IncidentAction:', err);
      }
    };

    fetchToken();
  }, [getAccessTokenSilently]);


  // MAKE ACKNOWLEDGE button - red
  // const acknowledgeIncident = (eventKey) => {

  //   dispatch(postIncidentState(incident._id, agency.agencyId, reportSchemaId, eventKey, tokenRef.current))
  //   const selectedState = reportStates.find(state => state._id === eventKey)
  //   if (selectedState) dispatch(updateIncidentStatus(incident._id, selectedState.report_status_id))
  // };

  // change previous acknowledge button to do two things if needed
  // MAKE NEW ACKNOWLEDGE button - red
  // MAKE ACKNOWLEDGE button - red
  const acknowledgeIncident = async (eventKey) => {
    try {
      // Ensure we have a token (same pattern as deleteIncident)
      if (!tokenRef.current) {
        tokenRef.current = await getAccessTokenSilently();
      }

      // 1) Existing behavior: set state & status to OPEN
      dispatch(
        postIncidentState(
          incident._id,
          agency.agencyId,
          reportSchemaId,
          eventKey,
          tokenRef.current
        )
      );

      const selectedState = reportStates.find((state) => state._id === eventKey);
      if (selectedState) {
        dispatch(
          updateIncidentStatus(
            incident._id,
            selectedState.report_status_id
          )
        );
      }

      // 2) NEW behavior: auto-send a User Note when Acknowledge is clicked
      if (autoAckEnabled && autoAckMessage && incident?._id) {
        const displayName =
          profile?.user_metadata?.name ||
          profile?.name ||
          'Dispatcher';

        const message = {
          type: 'note',                 // stays in the Notes stream
          display_name: displayName,
          message: autoAckMessage,
          sender_id: profile?.sub || 'system-auto-ack',
          sent: Date.now(),
          meta: {
            auto_acknowledge: true      // optional flag so BE/FE can recognize
          }
        };

        dispatch(
          postNoteMessage(
            incident._id,
            agency.agencyId,
            reportSchemaId,
            message,
            tokenRef.current
          )
        );
      }
    } catch (err) {
      console.error('[ACK] Failed to acknowledge incident / send auto note:', err);
      // We’re logging only; you could add a toast/alert here if desired
    }
  };
  /// end of new 20251216 AK feature

  const confirmAcknowledge = (eventKey) => {
    if (window.confirm('Are you sure you want to Acknowledge this report?\nWarning: this cannot be undone.')) {
      acknowledgeIncident(eventKey);
    }
  };

  const closeIncident = (eventKey) => {
    dispatch(postIncidentState(incident._id, agency.agencyId, reportSchemaId, eventKey, tokenRef.current))
    const selectedState = reportStates.find(state => state._id === eventKey)
    if (selectedState) dispatch(updateIncidentStatus(incident._id, selectedState.report_status_id))
  };

  const checkClose = (eventKey) => {
    if (window.confirm('Are you sure you wish to Close this report?\nWarning: this cannot be undone.')) {
      closeIncident(eventKey);
    }
  };

  const selectState = (eventKey) => {
    dispatch(postIncidentState(incident._id, agency?.agencyId, reportSchemaId, eventKey, tokenRef.current));
    const selectedState = reportStates.find(state => state._id === eventKey);
    if (selectedState) dispatch(updateIncidentStatus(incident._id, selectedState.report_status_id));
  };



  const selectChat = () => {
    if (isClosed) {
      alert('This chat session is closed for this incident.\nYou can review the messages under the Reports menu.');
      return;
    }
    dispatch(showModal('INCIDENT_CHAT', { incident, agencyId: agency?.agencyId, reportSchemaId }));
  };

  const selectNotes = () => {
    if (isClosed) {
      alert('This note session is closed for this incident. \nYou can review notes under the Reports menu.');
      return;
    }
    dispatch(showModal('INCIDENT_NOTES', { incident, agencyId: agency?.agencyId, reportSchemaId }));
  };


  // 20251107: added email report button
  const selectEmail = () => {
    // 📝 pass agencyDisplayName so the modal can render proper From/Subject
    dispatch(showModal('INCIDENT_EMAIL', { incident, agencyId: agency?.agencyId, reportSchemaId, agencyName, }));
  };


  const selectLog = () => {
    dispatch(showModal('INCIDENT_LOG', { incident, agencyId: agency?.agencyId, reportSchemaId }));
  };

  const selectResource = () => {
    dispatch(showModal('INCIDENT_RESOURCE', { incident, languageCode, agency }));
  };

  // const deleteIncident = () => {
  //   dispatch(deleteReport(incident._id, incident.agency_id, incident.report_schema_id));
  // };

  const deleteIncident = async () => {
    if (!tokenRef.current) {
      try { tokenRef.current = await getAccessTokenSilently(); }
      catch (e) { console.error('[DEL] token fetch failed', e); return; }
    }
    // debug log
    console.log('[MARK][UI][DEL] click', {
      rid: incident._id,
      aid: incident.agency_id,
      rsid: incident.report_schema_id,
      tokenPresent: !!tokenRef.current
    });

    dispatch(deleteReport(
      incident._id,
      incident.agency_id,
      incident.report_schema_id,
      tokenRef.current            // ← add this
    ));
  };

  const checkDelete = () => {
    if (window.confirm('Are you sure you want to delete this report?\nWarning: this cannot be undone.')) {
      deleteIncident();
    }
  };

  // added feature to block a reporter user
  const checkBlockReporter = () => {

    const authorizedBy = window.prompt(
      "Who authorized this reporter block?"
    );

    if (!authorizedBy) return;

    const reason = window.prompt(
      "Reason for blocking this reporter?"
    );

    if (!reason) return;

    if (
      window.confirm(
        `Block reporter for incident ${incident.display_title}?\n\n` +
        `Reporter user_id:\n${incident.user_id}\n\n` +
        `Authorized by: ${authorizedBy}\n` +
        `Reason: ${reason}\n\n` +
        "Future reports from this app account will be rejected."
      )
    ) {
      blockReporter(authorizedBy, reason);
    }
  };

  const checkUnblockReporter = () => {
    const reason = window.prompt("Reason for unblocking this reporter?");
    if (!reason) return;

    if (
      window.confirm(
        `Unblock reporter for incident ${incident.display_title}?\n\n` +
        `Reporter user_id:\n${incident.user_id}\n\n` +
        `Reason: ${reason}`
      )
    ) {
      unblockReporter(reason);
    }
  };

  const unblockReporter = async (reason) => {
    if (!tokenRef.current) {
      try {
        tokenRef.current = await getAccessTokenSilently();
      } catch (e) {
        console.error("[UNBLOCK_REPORTER] token fetch failed", e);
        return;
      }
    }

    try {
      const url = `${Globals.apiEndpoint}/dashboard/agencies/${incident.agency_id}/report_summaries/${incident._id}/unblock_reporter`;

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenRef.current}`,
        },
        body: JSON.stringify({
          unblock_reason: reason,
          unblocked_by_user_id: profile?.sub || null,
          unblocked_by_name:
            profile?.name || profile?.nickname || profile?.email || "",
        }),
      });

      const json = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        alert(json?.message || "Failed to unblock reporter");
        return;
      }

      setReporterBlocked(false);
      alert(json?.message || "Reporter unblocked");
    } catch (e) {
      console.error("[UNBLOCK_REPORTER] failed", e);
      alert("Network error unblocking reporter");
    }
  };

  const blockReporter = async (authorizedBy, reason) => {
    if (!tokenRef.current) {
      try {
        tokenRef.current = await getAccessTokenSilently();
      } catch (e) {
        console.error("[BLOCK_REPORTER] token fetch failed", e);
        return;
      }
    }

    try {
      const url = `${Globals.apiEndpoint}/dashboard/agencies/${incident.agency_id}/report_summaries/${incident._id}/block_reporter`;

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${tokenRef.current}`,
        },
        body: JSON.stringify({
          reason,
          authorized_by_name: authorizedBy,
          blocked_by_user_id: profile?.sub || null,
          blocked_by_name:
            profile?.name || profile?.nickname || profile?.email || "",
        }),
      });

      const json = await resp.json().catch(() => ({}));

      if (!resp.ok) {
        alert(json?.message || "Failed to block reporter");
        return;
      }
      setReporterBlocked(true);
      alert(json?.message || "Reporter blocked");
    } catch (e) {
      console.error("[BLOCK_REPORTER] failed", e);
      alert("Network error blocking reporter");
    }
  };


  const selectedAgencyId =
    agency?._id ||
    agency?.details?._id ||
    agency?.agencyId ||
    agency?.selectedAgencyId ||
    incident?.agency_id;

  const canBlockReporter =
    profile?.app_metadata?.is_admin === true ||
    (profile?.app_metadata?.agency_permissions || []).some(
      (p) =>
        (p.id === "*" || p.id === selectedAgencyId) &&
        p.block_reporter === true
    );

  const canUseUnitResource =
    profile?.app_metadata?.is_admin === true ||
    (profile?.app_metadata?.agency_permissions || []).some(
      (p) =>
        (p.id === "*" || p.id === selectedAgencyId) &&
        p.unit_resource === true
    );


  // Check whether this user has permission to use the Email Report feature and set the flag.
  const canEmailIncident =
  (profile?.app_metadata?.agency_permissions || []).some(
    (p) =>
      (p.id === "*" || p.id === selectedAgencyId) &&
      p.email_incident === true
  );


  const setIncidentChatBackground = () => {
    const chatSummary = incident?.chat_summary;
    if (!chatSummary) return null;

    if (chatSummary.status === 'open') {
      return chatSummary.user_last_received > chatSummary.dashboard_last_received
        ? 'user-recent'
        : 'dashboard-recent';
    }

    if (chatSummary.status === 'closed') {
      return (chatSummary.user_last_received || chatSummary.dashboard_last_received) ? 'chat-closed' : null;
    }

    return null;
  };


  const loadReporterBlockStatus = async () => {
    if (!canBlockReporter || !incident?._id || !incident?.agency_id) return;

    setCheckingBlockStatus(true);

    try {
      if (!tokenRef.current) {
        tokenRef.current = await getAccessTokenSilently();
      }

      const url = `${Globals.apiEndpoint}/dashboard/agencies/${incident.agency_id}/report_summaries/${incident._id}/block_reporter`;

      const resp = await fetch(url, {
        headers: {
          Authorization: `Bearer ${tokenRef.current}`,
        },
      });

      const json = await resp.json().catch(() => ({}));

      if (resp.ok) {
        setReporterBlocked(!!json.blocked);
      }
    } catch (e) {
      console.error("[BLOCK_REPORTER] status check failed", e);
    } finally {
      setCheckingBlockStatus(false);
    }
  };

  useEffect(() => {
    loadReporterBlockStatus();
  }, [incident?._id, canBlockReporter]);
  ///

  // THIS IS DIFF FROM PROD RIGHT NOW. MIGHT DO THE SAME BUT LOGIC DIFF
  // const setIncidentChatBackground = () => {
  //   const s = incident?.chat_summary?.status;
  //   if (s === 'ended' || s === 'closed') return 'chat-closed'; // blue
  //   if (s === 'open') {
  //     return incident.chat_summary.user_last_received > incident.chat_summary.dashboard_last_received
  //       ? 'user-recent'
  //       : 'dashboard-recent';
  //   }
  //   return null;
  // };

  const setNoteButtonBackground = () => {
    return incident?.note_summary?.dashboard_last_received ? 'note-received' : null;
  };

  const setLogButtonBackground = () => {
    return incident?.internal_message_summary?.last_received ? 'log-received' : null;
  };


   // NEW 20260814: add email report button
   const setEmailButtonBackground = () => {
     return incident?.email_summary?.last_sent ? 'email-sent' : null;
  }; 


  const circle = selectedStatus?.title ? (
    <div className='status-dropdown'>
      <div className='status-circle' style={{ background: selectedStatus.color }} />
      {selectedStatus.title[languageCode] || 'Closed'}
    </div>
  ) : (
    <div className='status-dropdown'>Select</div>
  );

  return (
    <div>

      <section className="status-container">
        <div className="action-status">


          {/* <button
    className={`btn btn-rectangle ${incident.state === reportStates[0]?._id ? 'btn-danger' : 'btn-secondary'}`}
    style={{ padding: '4px 8px', fontSize: '0.8rem', height: '30px', minWidth: '90px' }}

      id={reportStates[1]?._id}
      onClick={() => confirmAcknowledge(reportStates[1]._id)}
      disabled={incident.state !== reportStates[0]?._id}
    >
      {incident.state === reportStates[0]?._id ? 'Acknowledge' : 'Acknowledged'}
    </button>

    <button
      className={`btn btn-rectangle ${(incident.state === reportStates[1]?._id) ? 'btn-danger' : 'btn-secondary'}`}
      id={reportStates[2]?._id}
      onClick={() => checkClose(reportStates[2]._id)}
      disabled={incident.state !== reportStates[1]?._id}
    >
      {(incident.state === reportStates[1]?._id || incident.state === reportStates[0]?._id)
        ? 'Close'
        : 'Closed'}
    </button> */}
          <button
            className={`btn btn-rectangle ${isNew ? 'btn-danger' : 'btn-secondary'}`}
            style={{ padding: '4px 8px', fontSize: '0.8rem', height: '30px', minWidth: '90px' }}
            onClick={() => isNew && confirmAcknowledge(OPEN_STATE_ID)}   // send OPEN state id
            disabled={!isNew}
          >
            {isNew ? 'Acknowledge' : 'Acknowledged'}
          </button>

          <button
            className={`btn btn-rectangle ${isOpen ? 'btn-danger' : 'btn-secondary'}`}
            onClick={() => isOpen && checkClose(CLOSED_STATE_ID)}        // send CLOSED state id
            disabled={!isOpen}
          >
            {(isOpen || isNew) ? 'Close' : 'Closed'}
          </button>




          {/* Status Pill (Not dropdown anymore) */}
          <div className="status-display">
            {selectedStatus?.color && (
              <div
                className="status-circle"
                style={{ backgroundColor: selectedStatus.color }}
              />
            )}
            <span>{selectedStatus?.title?.[languageCode] || 'Status Unknown'}</span>
          </div>
        </div>
      </section>



      <section className="action-container">
        <div className='action-chat'>
          <div>User<br />Chat</div>
          <button className={`btn btn-sm ${setIncidentChatBackground() || ''}`} id='chat-button' onClick={selectChat}>
            <i id='chat-icon' className='material-icons'>chat</i>
          </button>
        </div>


        {!hideUserNoteButton && (
          <div className='action-note'>
            <div>User<br />Note</div>
            <button className={`btn btn-sm ${setNoteButtonBackground() || ''}`} id='note-button' onClick={selectNotes}>
              <i id='note-icon' className='material-icons'>note_add</i>
            </button>
          </div>
        )}


{/* Check to make sure user can see/use the email button */}
{canEmailIncident && (
      <div className='action-email'>
  <div>Email<br />Report</div>
  <button
    className={`btn btn-sm ${setEmailButtonBackground() || ''}`}
    id='email-button'
    onClick={selectEmail}
  >
    <i id='email-icon' className='material-icons'>email</i>
  </button>
  </div>
)}


        <div className='action-log'>
          <div>Dispatch<br />Log</div>
          <button className={`btn btn-sm ${setLogButtonBackground() || ''}`} id='log-button' onClick={selectLog}>
            <i id='log-icon' className='material-icons'>view_headline</i>
          </button>
        </div>
        {canUseUnitResource && (
          <div className='action-resource'>
            <div>Unit<br />Resource</div>
            <button className="btn btn-sm" id='resource-button' onClick={selectResource}>
              <i id='log-icon' className='material-icons'>local_police</i>
            </button>
          </div>
        )}
        {canBlockReporter && (
          <div className='action-block'>
            <div>
              {reporterBlocked ? (
                <>
                  Unblock<br />User
                </>
              ) : (
                <>
                  Block<br />User
                </>
              )}
            </div>

            <button
              className="btn btn-sm"
              id='block-user-button'
              style={
                reporterBlocked
                  ? {
                    backgroundColor: "#dc3545",
                    color: "#fff",
                    borderColor: "#dc3545",
                  }
                  : {}
              }

              onClick={reporterBlocked ? checkUnblockReporter : checkBlockReporter}
              disabled={checkingBlockStatus}
            >
              <i id='block-user-icon' className='material-icons'>
                {reporterBlocked ? "lock_open" : "block"}
              </i>
            </button>
          </div>
        )}


        {myAdmin && (
          <div className='action-delete'>
            <div>Incident<br />Delete</div>
            <button className="btn btn-sm" id='delete-button' onClick={checkDelete}>
              <i id='delete-icon' className='material-icons'>delete</i>
            </button>
          </div>
        )}
      </section>
    </div>
  );

};
IncidentAction.propTypes = {
  reportSchemaId: PropTypes.string,
  languageCode: PropTypes.string,

};

export default IncidentAction;